import { useCallback } from 'react'

import { cloneDeep, isNil } from 'lodash-es'

import {
  CustomerCopy,
  CustomerTreatmentOption,
  CustomerTreatmentsOptionLocation,
  TREATMENT_OPTIONS_TYPE,
  TREATMENT_RECOMMENDATION_TIER,
} from '@lyrahealth-inc/shared-app-logic'

type AddCustomerTreatmentOptionsParams = {
  searcherAge: number
  location: CustomerTreatmentsOptionLocation
  treatmentOptions: TREATMENT_OPTIONS_TYPE[]
  customerCopy?: CustomerCopy
  tier?: TREATMENT_RECOMMENDATION_TIER
  employeeGroup?: string
  eligibleTreatmentOptions?: (TREATMENT_OPTIONS_TYPE | string)[] // this comes from LPA, no guarantee that it will be a TREATMENT_OPTIONS_TYPE, which is kind of a smaller subset curated list for FE
}

/**
 * Normalize employee group to handle variations including:
 * Case differences, Whitespace, Commas, Different dash types - hyphen, en-dash, em-dash
 */
const normalizeEmployeeGroup = (value: string): string => {
  return value.toLowerCase().replace(/\s+/g, '').replace(/,/g, '').replace(/[-–—]/g, '').trim()
}

/** Takes in a list of treatment options and adds in the customer specific options based on `customerCopy` */
export const useAddCustomerTreatmentOptions = () => {
  return useCallback(
    ({
      searcherAge,
      location,
      treatmentOptions,
      customerCopy,
      tier,
      employeeGroup,
      eligibleTreatmentOptions,
    }: AddCustomerTreatmentOptionsParams) => {
      const treatmentOptionsCloned = cloneDeep(treatmentOptions)
      const configForSpecificTreatmentOptions = {}
      const customerTreatmentOptions: CustomerTreatmentOption[] = customerCopy?.treatmentOptions || []
      customerTreatmentOptions.forEach((item: CustomerTreatmentOption) => {
        // variable are true if not defined
        const insertionIndex = item.optionRanking[location]
        const meetsAgeRequirement = searcherAge > (item.config?.minAge ?? 0)
        const hasValidRanking = !isNil(insertionIndex)

        const addTreatment = () => {
          if (insertionIndex != null) {
            treatmentOptionsCloned.splice(insertionIndex, 0, item.option)
            configForSpecificTreatmentOptions[item.option] = item.config
          }
        }

        // if age requirement is not met or ranking is not valid, skip (not defined makes it valid)
        if (!meetsAgeRequirement || !hasValidRanking) {
          return
        }

        // age requirement is met and ranking is valid here (not defined makes it valid)
        if (!item.displayRules) {
          addTreatment()
          return
        }

        // check display rules
        const tierCheckPasses =
          !item.displayRules.showForTiers?.length || (tier != null && item.displayRules.showForTiers.includes(tier))
        const treatmentOptionsCheckPasses =
          !item.displayRules.showForTreatmentOptions?.length ||
          item.displayRules.showForTreatmentOptions.some((option) => (eligibleTreatmentOptions ?? []).includes(option))
        const employeeGroupCheckPasses =
          !item.displayRules.eligibility?.employeeGroup?.length ||
          (employeeGroup != null &&
            item.displayRules.eligibility.employeeGroup.some(
              (group) => normalizeEmployeeGroup(employeeGroup) === normalizeEmployeeGroup(group),
            ))

        if (tierCheckPasses && treatmentOptionsCheckPasses && employeeGroupCheckPasses) {
          addTreatment()
        }
      })
      return { treatmentOptions: treatmentOptionsCloned, configForSpecificTreatmentOptions }
    },
    [],
  )
}
